import { WagmiProvider, createConfig, http, fallback } from 'wagmi';
import { mainnet, base, polygon, arbitrum, bsc, avalanche, optimism } from 'wagmi/chains';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RainbowKitProvider, getDefaultConfig, darkTheme } from '@rainbow-me/rainbowkit';
import '@rainbow-me/rainbowkit/styles.css';

// Each chain gets a fallback() transport with multiple public RPC endpoints.
// Wagmi tries them in order and rotates on failure. Previously we used a
// single hardcoded Alchemy key here — when that key was rate-limited or
// stale, every read (allowance check, balance, etc) failed with the generic
// "HTTP request failed" viem error and nothing else worked. Public RPCs are
// free and don't require a key.
const ALCHEMY_KEY = import.meta.env.VITE_ALCHEMY_KEY || 'KcMVBwqXm_SdmO0PmUYPe';

const transportFor = (alchemySubdomain, publicUrls) => fallback([
  http(`https://${alchemySubdomain}.g.alchemy.com/v2/${ALCHEMY_KEY}`),
  ...publicUrls.map(u => http(u)),
]);

const config = getDefaultConfig({
  appName: 'haythix',
  projectId: import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || '5a19d3747551a82d432b9d7aa25448f1',
  chains: [mainnet, base, polygon, arbitrum, bsc, avalanche, optimism],
  transports: {
    [mainnet.id]:   transportFor('eth-mainnet',       ['https://eth.llamarpc.com',           'https://rpc.ankr.com/eth',         'https://cloudflare-eth.com']),
    [base.id]:      transportFor('base-mainnet',      ['https://mainnet.base.org',           'https://base.llamarpc.com',        'https://base.publicnode.com']),
    [polygon.id]:   transportFor('polygon-mainnet',   ['https://polygon-rpc.com',            'https://polygon.llamarpc.com',     'https://rpc.ankr.com/polygon']),
    [arbitrum.id]:  transportFor('arb-mainnet',       ['https://arb1.arbitrum.io/rpc',       'https://arbitrum.llamarpc.com',    'https://rpc.ankr.com/arbitrum']),
    [bsc.id]:       transportFor('bnb-mainnet',       ['https://bsc-dataseed.binance.org',   'https://bsc.llamarpc.com',         'https://rpc.ankr.com/bsc']),
    [avalanche.id]: transportFor('avalanche-mainnet', ['https://api.avax.network/ext/bc/C/rpc', 'https://avalanche.llamarpc.com', 'https://rpc.ankr.com/avalanche']),
    [optimism.id]:  transportFor('opt-mainnet',       ['https://mainnet.optimism.io',        'https://optimism.llamarpc.com',    'https://rpc.ankr.com/optimism']),
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Re-fetch when user returns to the tab (PWA snappiness)
      refetchOnWindowFocus: true,
      // Pause polling when tab is hidden — saves ~70% of API calls
      refetchIntervalInBackground: false,
      // Always treat data as stale so refetchOnWindowFocus actually refetches
      staleTime: 0,
      // Cache for 5 min so back-navigation is instant
      gcTime: 5 * 60 * 1000,
      // Don't retry forever on network failure (mobile flaky connections)
      retry: 1,
      retryDelay: 1000,
    },
  },
});

export default function WalletProvider({ children }) {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider theme={darkTheme({
          accentColor: '#F4BE37',
          accentColorForeground: '#000',
          borderRadius: 'medium',
        })}>
          {children}
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}
